import {
  map,
  find,
  orderBy,
  sortBy,
  isNil,
  maxBy,
  minBy,
  filter,
  isEmpty,
  head,
  range,
} from 'lodash';

const customSubject = {
  id: 0,
  name: 'common.basic_subject',
  color_code: '#00C896',
};

export function getCatSubjects(state, getters) {
  const grade = !isEmpty(getters.getSelectedChild) ?
    getters.getSelectedChildGrade :
    getters.currentUserGrade;

  return filter(getters.getSubjectMap, item => (
    item.grade_lower_bound <= grade &&
    item.grade_upper_bound >= grade
  ));
}

export function getDomains(state) {
  return map(state.catDomains, domain => (
    {
      cat: domain.cat,
      domain_id: domain.id,
      domain_name: domain.name,
      proficiency_score: domain.proficiency_score,
      rank: domain.rank,
      subject_id: domain.subject_id,
    }
  ));
}

export function getCatDomains(state, getters) {
  return map(getters.getDomains, domain => ({
    ...domain,
    ...find(getters.getCatSubjects, subject => (
      subject.id === domain.subject_id
    )),
  }));
}

export function getActiveAssignments(state) {
  return filter(state.activeAssignments, assignment => (
    !assignment.submissions[0].is_submitted
  )) || [];
}

export function getFilterLecturesSubjects(state, getters) {
  return [
    customSubject,
    ...getters.getSubjectMap,
  ];
}

export function getFilterSubjects(state, getters) {
  return [
    customSubject,
    ...getters.getCatSubjects,
  ];
}

export function getSelectedSubjectId(state) {
  return state.selectedSubjectId || 0;
}

export function getFilterProficiencyHistory(state, getters) {
  if (!isEmpty(getters.getSelectedChildId)) {
    return state.childProficiencyHistory[getters.getSelectedChildId] || {};
  }
  return state.filterProficiencyHistory || {};
}

export function getCurrentScore(state, getters) {
  return (getters.getFilterProficiencyHistory[getters.getSelectedSubjectId] &&
  getters.getFilterProficiencyHistory[getters.getSelectedSubjectId].current_score) || 0;
}

export function getAverageScore(state, getters) {
  return (getters.getFilterProficiencyHistory[getters.getSelectedSubjectId] &&
    getters.getFilterProficiencyHistory[getters.getSelectedSubjectId].cl_average_score) || 0;
}

export function getAverageScoreRate(state, getters) {
  return Number((getters.getAverageScore / state.classtingTotalScore) * 100) || 0;
}

export function getCurrentScoreRate(state, getters) {
  return Number((getters.getCurrentScore / state.classtingTotalScore) * 100) || 0;
}

export function getProficiencyHistory(state, getters) {
  const proficiencyHistory = (getters.getFilterProficiencyHistory[getters.getSelectedSubjectId] &&
    getters.getFilterProficiencyHistory[getters.getSelectedSubjectId].history) || [];

  const todayScore = {
    proficiency_score: getters.getCurrentScore,
    updated_at: new Date(),
  };

  return [...proficiencyHistory && proficiencyHistory.slice(0, 3), todayScore];
}

export function getOrderProficiencyHistory(state, getters) {
  const sortTargetName = 'updated_at';
  const limitScore = 4;
  const proficiencyHistory = getters.getProficiencyHistory;

  if (proficiencyHistory.length < limitScore) {
    const chartRange = limitScore - proficiencyHistory.length;
    const defaultScore = {
      proficiency_score: 0,
      updated_at: '',
    };

    return orderBy([
      ...map(range(chartRange), () => defaultScore),
      ...proficiencyHistory,
    ], sortTargetName, ['asc']);
  }

  return orderBy(proficiencyHistory, sortTargetName, ['asc']);
}

export function getLastScore(state, getters) {
  const lastScoreIndex = getters.getOrderProficiencyHistory.length - 2;
  return getters.getOrderProficiencyHistory &&
    getters.getOrderProficiencyHistory[lastScoreIndex];
}

export function getChangedScore(state, getters) {
  if (getters.getLastScore && getters.getLastScore.proficiency_score > 0) {
    return getters.getCurrentScore - (getters.getLastScore &&
      getters.getLastScore.proficiency_score);
  }
  return 0;
}

export function getFilterProficiencyRank(state, getters) {
  if (!isEmpty(getters.getSelectedChild)) {
    return (state.childProficiencyRank[getters.getSelectedChildId])
      || {};
  }

  return state.filterProficiencyRank || {};
}

export function getProficiencyRank(state, getters) {
  if (getters.getDomainProficiencyRank) {
    return head(getters.getDomainProficiencyRank) || {};
  }

  return (getters.getFilterProficiencyRank[getters.getSelectedSubjectId] &&
    getters.getFilterProficiencyRank[getters.getSelectedSubjectId][0]) || {};
}

export function getPreviousProficiencyRank(state, getters) {
  if (getters.getDomainProficiencyRank) {
    return (getters.getDomainProficiencyRank && getters.getDomainProficiencyRank[1]) || {};
  }

  return (getters.getFilterProficiencyRank[getters.getSelectedSubjectId] &&
    getters.getFilterProficiencyRank[getters.getSelectedSubjectId][1]) || {};
}

export function getSchoolName(state, getters) {
  return getters.getProficiencyRank.school_name;
}

export function getSchoolPosition(state, getters) {
  return getters.getProficiencyRank.school_position;
}

export function getSchoolTotal(state, getters) {
  return getters.getProficiencyRank.school_total;
}

export function isSchoolTotal(state, getters) {
  return getters.getSchoolTotal >= 10;
}

export function getSchoolRate(state, getters) {
  return Number(((getters.getSchoolPosition /
    getters.getSchoolTotal) * 100)) || 0;
}

export function getPreviousSchoolPosition(state, getters) {
  return getters.getPreviousProficiencyRank.school_position || 0;
}

export function getPreviousSchoolTotal(state, getters) {
  return getters.getPreviousProficiencyRank.school_total || 0;
}

export function getPreviousSchoolRate(state, getters) {
  return Number(((getters.getPreviousSchoolPosition /
      getters.getPreviousSchoolTotal) * 100)) || 0;
}

export function getGradePosition(state, getters) {
  return getters.getProficiencyRank.grade_position || 0;
}

export function getGradeTotal(state, getters) {
  return getters.getProficiencyRank.grade_total || 0;
}

export function isGradeTotal(state, getters) {
  return getters.getGradeTotal >= 10;
}

export function getGradeRate(state, getters) {
  return Number(((getters.getGradePosition /
      getters.getGradeTotal) * 100)) || 0;
}

export function getChangedSchoolRate(state, getters) {
  if (getters.getPreviousSchoolRate === 0) {
    return 0;
  }

  return Number((((getters.getPreviousSchoolRate - getters.getSchoolRate) * 100)
    / getters.getPreviousSchoolRate)) || 0;
}

export function getPreviousGradePosition(state, getters) {
  return getters.getPreviousProficiencyRank.grade_position;
}

export function getPreviousGradeTotal(state, getters) {
  return getters.getPreviousProficiencyRank.grade_total;
}

export function getPreviousGradeRate(state, getters) {
  return Number(((getters.getPreviousGradePosition / getters.getPreviousGradeTotal) * 100)) || 0;
}

export function getChangedGradeRate(state, getters) {
  if (getters.getPreviousGradeRate === 0) {
    return 0;
  }

  return Number((((getters.getPreviousGradeRate - getters.getGradeRate) * 100) /
    getters.getPreviousGradeRate)) || 0;
}

export function getChangedScores(state, getters) {
  return map(getters.getFilterProficiencyHistory, (item, index) => {
    if (isNil(item.current_score)) {
      return {
        subject_id: Number(index),
        changed_score: 0,
      };
    }
    if (Number(index) !== 0) {
      const historyScore = head(orderBy(item.history, ['desc'], 'updated_at')) || {};
      return {
        subject_id: Number(index),
        subject_name: find(getters.getCatSubjects, i =>
            i.id === Number(index)).name,
        changed_score: item.current_score - historyScore.proficiency_score,
      };
    }
    return false;
  });
}

export function getGrowthSubject(state, getters) {
  return maxBy(getters.getChangedScores, 'changed_score');
}

export function getNotGrowthSubject(state, getters) {
  return minBy(getters.getChangedScores, 'changed_score');
}

export function getGrowthScore(state, getters) {
  return getters.getGrowthSubject && getters.getGrowthSubject.changed_score;
}

export function getGrowthSubjectName(state, getters) {
  return getters.getGrowthSubject && getters.getGrowthSubject.subject_name;
}

export function getNotGrowthScore(state, getters) {
  return getters.getNotGrowthSubject && getters.getNotGrowthSubject.changed_score;
}

export function getNotGrowthSubjectName(state, getters) {
  return getters.getNotGrowthSubject && getters.getNotGrowthSubject.subject_name;
}

export function getRandomComments(state) {
  return state.catDescription.isNotAssignment;
}

export function getCatReturnVisitComment(state) {
  return state.returnVisit;
}

export function getFirstSelectedChild(state, getters) {
  return head(getters.userChildrenList);
}

export function getChildProficiencyHistory(state) {
  return state.childProficiencyHistory || {};
}

export function getChildProficiencyRank(state) {
  return state.childProficiencyRank || {};
}

export function getSelectedChild(state) {
  return state.selectedChild || {};
}

export function getSelectedChildGrade(state, getters) {
  const tGrade = getters.getSelectedChild.t_grade;

  if (tGrade && tGrade > 6 && tGrade < 19) {
    return tGrade - 6;
  }

  return 0;
}

export function isSupportedChildGrade(state, getters) {
  return getters.getSelectedChildGrade > 2;
}

export function getSelectedChildId(state, getters) {
  if (!isEmpty(getters.getRouteQuery.child_id)) {
    return getters.getRouteQuery.child_id;
  }

  return getters.getSelectedChild.id;
}

export function getSelectedLectureSubjectId(state) {
  return state.selectedLectureSubjectId || 0;
}

export function getLecturesType(state) {
  return state.lecturesType;
}

export function getVideoLectures(state, getters) {
  return state.filterVideoLectures[getters.getSelectedLectureSubjectId];
}

export function getRecommendVideoLectures(state) {
  return state.recommendVideoLectures;
}

export function getVideoLecturePosition(state, getters) {
  return state.filterVideoLecturesPosition[getters.getSelectedLectureSubjectId];
}

export function getCardLectures(state, getters) {
  return state.filterCardLectures[getters.getSelectedLectureSubjectId];
}

export function getRecommendCardLectures(state) {
  return state.recommendCardLectures;
}

export function getSubjectMap(state) {
  return state.subjectMap;
}

export function getVideoLectureSubjects(state, getters) {
  return [
    customSubject,
    ...filter(sortBy(getters.getSubjectMap, ['grade_lower_bound'], ['grade_upper_bound']), item => (
      ((item.grade_lower_bound <= getters.currentUserGrade
      && item.grade_upper_bound >= getters.currentUserGrade)
      || item.grade_upper_bound === null)
      && item.has_video_lectures
    )),
  ];
}

export function getCardLectureSubjects(state, getters) {
  return [
    customSubject,
    ...filter(sortBy(getters.getSubjectMap, ['grade_lower_bound'], ['grade_upper_bound']), item => (
      ((item.grade_lower_bound <= getters.currentUserGrade
      && item.grade_upper_bound >= getters.currentUserGrade)
      || item.grade_upper_bound === null)
      && item.has_card_lectures
    )),
  ];
}

export function getCardLecturePosition(state, getters) {
  return state.filterCardLecturesPosition[getters.getSelectedLectureSubjectId];
}

export function getBusy(state) {
  return state.busy;
}



// WEBPACK FOOTER //
// ./src/app/cat-main/store/getters.js